import { memo, useMemo } from 'react';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { NavigationIcon } from '@cbhq/cds-web/icons';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { HeroSquare, SpotSquare } from '@cbhq/cds-web/illustrations';
import { Box, Grid, HStack, VStack } from '@cbhq/cds-web/layout';
import { Link, TextHeadline, TextLabel2, TextTitle3 } from '@cbhq/cds-web/typography';

import { useIsBaseAAEnabled } from ':cloud/hooks/featureFlags/useIsBaseAAEnabled';
import { ProductType } from ':cloud/types/ts_types';
import {
  BASE_DEMO_APP_LINK,
  BASE_DISCORD,
  CLOUD_DOCS,
  PAYMASTER_BUNDLER_DOCS_LINK,
  WAAS_WELCOME_LINK,
} from ':cloud/utils/links';
import { LinkButton } from ':cloud/widgets/sharedcomponents/LinkButton';

type ResourceSectionContentProps = {
  productType?: ProductType;
};

function ResourceSectionContent({ productType }: ResourceSectionContentProps) {
  const { isDesktop } = useBreakpoints();
  const isWaasProject = productType === 'waas';
  const isBaseProject = productType === 'base';

  const isBaseAAEnabled = useIsBaseAAEnabled();

  const responsiveTemplateColumns = useMemo(() => {
    return isDesktop ? '1fr 1fr 1fr' : '1fr';
  }, [isDesktop]);

  return (
    <>
      <TextTitle3 as="h2" spacingBottom={5}>
        Resources
      </TextTitle3>

      <Grid
        templateColumns={responsiveTemplateColumns}
        gap={3}
        maxWidth={isDesktop ? undefined : '100%'}
      >
        {isWaasProject && (
          <VStack borderRadius="roundedLarge" borderColor="line" spacing={3}>
            <TextHeadline as="h4" spacingBottom={3}>
              Download
            </TextHeadline>
            <Link to={WAAS_WELCOME_LINK} openInNewWindow color="foreground">
              <HStack justifyContent="space-between" alignItems="center">
                <HStack alignItems="center">
                  <Box borderRadius="roundedFull" background="backgroundAlternate" spacing={2}>
                    <NavigationIcon name="cloud" size="s" />
                  </Box>
                  <TextHeadline spacingHorizontal={2} as="h4">
                    Wallet as a Service SDK
                  </TextHeadline>
                </HStack>
                <Icon name="download" size="s" color="foreground" />
              </HStack>
            </Link>
          </VStack>
        )}

        {isWaasProject && (
          <VStack borderRadius="roundedLarge" borderColor="line" spacing={3}>
            <TextHeadline as="h4" spacingBottom={3}>
              Docs
            </TextHeadline>
            <Link to={WAAS_WELCOME_LINK} openInNewWindow color="foreground">
              <HStack justifyContent="space-between" alignItems="center" height={36}>
                <TextHeadline as="h4">Wallet as a Service</TextHeadline>
                <Icon name="externalLink" size="s" color="foreground" />
              </HStack>
            </Link>
          </VStack>
        )}

        {isBaseProject && (
          <HStack
            borderRadius="roundedLarge"
            borderColor="line"
            justifyContent="space-between"
            spacing={5}
            gap={2}
          >
            <VStack justifyContent="space-between">
              <VStack>
                <TextHeadline as="h4" spacingBottom={1}>
                  Docs
                </TextHeadline>
                <TextLabel2 as="p" color="foregroundMuted">
                  Use our API references and detailed guides curated for developers to build your
                  wallet.
                </TextLabel2>
              </VStack>
              <LinkButton text="Docs" url={PAYMASTER_BUNDLER_DOCS_LINK} />
            </VStack>
            <VStack justifyContent="center" alignItems="center">
              <SpotSquare name="guideNftDefi" />
            </VStack>
          </HStack>
        )}

        {isBaseProject && (
          <HStack
            borderRadius="roundedLarge"
            borderColor="line"
            justifyContent="space-between"
            spacing={5}
            gap={2}
          >
            <VStack justifyContent="space-between">
              <VStack>
                <TextHeadline as="h4" spacingBottom={1}>
                  Forum
                </TextHeadline>
                <TextLabel2 as="p" color="foregroundMuted">
                  Check out news, insights, and knowledge from the community.
                </TextLabel2>
              </VStack>
              <LinkButton text="Discord" url={BASE_DISCORD} />
            </VStack>
            <VStack justifyContent="center" alignItems="center">
              <SpotSquare name="decentralization" />
            </VStack>
          </HStack>
        )}

        {isBaseProject && isBaseAAEnabled && (
          <HStack
            borderRadius="roundedLarge"
            borderColor="line"
            justifyContent="space-between"
            spacing={5}
            gap={2}
          >
            <VStack justifyContent="space-between">
              <VStack>
                <TextHeadline as="h4" spacingBottom={1}>
                  Demo
                </TextHeadline>
                <TextLabel2 as="p" color="foregroundMuted">
                  View our demo to see the Base Paymaster & Bundler in action.
                </TextLabel2>
              </VStack>
              <LinkButton text="Demo" url={BASE_DEMO_APP_LINK} />
            </VStack>
            <VStack justifyContent="center" alignItems="center">
              <HeroSquare name="dappsGaming" scaleMultiplier={0.4} />
            </VStack>
          </HStack>
        )}

        {!productType && (
          <>
            <HStack
              borderRadius="roundedLarge"
              borderColor="line"
              justifyContent="space-between"
              spacing={5}
              gap={2}
            >
              <VStack justifyContent="space-between">
                <VStack>
                  <TextHeadline as="h4" spacingBottom={1}>
                    Developer Platform Docs
                  </TextHeadline>
                  <TextLabel2 as="p" color="foregroundMuted">
                    Use our API references and detailed guides curated for developers to build your
                    wallet.
                  </TextLabel2>
                </VStack>
                <LinkButton text="Docs" url={CLOUD_DOCS} />
              </VStack>
              <VStack justifyContent="center" alignItems="center">
                <SpotSquare name="guideNftDefi" />
              </VStack>
            </HStack>
            <HStack
              borderRadius="roundedLarge"
              borderColor="line"
              justifyContent="space-between"
              spacing={5}
              gap={2}
            >
              <VStack justifyContent="space-between">
                <VStack>
                  <TextHeadline as="h4" spacingBottom={1}>
                    Community
                  </TextHeadline>
                  <TextLabel2 as="p" color="foregroundMuted">
                    Check out news, insights, and knowledge from the community.
                  </TextLabel2>
                </VStack>
                <LinkButton text="Forum" url={BASE_DISCORD} />
              </VStack>
              <VStack justifyContent="center" alignItems="center">
                <SpotSquare name="decentralization" />
              </VStack>
            </HStack>
          </>
        )}
      </Grid>
    </>
  );
}

export const ResourceSection = memo(ResourceSectionContent);
